/**
 * Which drawing is under the pointer, by geometry — for pinning a note to it.
 *
 * klinecharts' own hit test only reaches ±2px of a line, and it cannot help at all while a
 * callout's tail is being dragged: the handle holds pointer capture, so the engine never sees
 * the pointer. This reaches ±6px (the same as trade-idea levels) from the drawing's points
 * converted to pixels. Pure, so it is unit-tested on its own.
 *
 * Trade-idea levels are not here — the Workbench's `tradeLevelAt` owns them.
 */

export interface Px {
  x: number
  y: number
}

/** How close (px) the pointer must be for a pin to take a drawing. */
export const PIN_HIT_TOL = 6

const LEG_MARKERS = ['buyEntry', 'sellEntry', 'exitPoint']

/** Distance from p to the line through a→b, with the projection clamped to [lo, hi] along it. */
function distAlong(p: Px, a: Px, b: Px, lo: number, hi: number): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return Math.hypot(p.x - a.x, p.y - a.y)
  const t = Math.min(hi, Math.max(lo, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2))
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

const distToSegment = (p: Px, a: Px, b: Px) => distAlong(p, a, b, 0, 1)
const distToRay = (p: Px, a: Px, b: Px) => distAlong(p, a, b, 0, Infinity)
const distToLine = (p: Px, a: Px, b: Px) => distAlong(p, a, b, -Infinity, Infinity)

/** 0 inside the box the two points span, else the distance to its edge. */
function distToBox(p: Px, a: Px, b: Px): number {
  const dx = Math.max(Math.min(a.x, b.x) - p.x, 0, p.x - Math.max(a.x, b.x))
  const dy = Math.max(Math.min(a.y, b.y) - p.y, 0, p.y - Math.max(a.y, b.y))
  return Math.hypot(dx, dy)
}

/**
 * How far (px) the pointer is from a drawing, as the pin should judge it — or null for an
 * overlay this module does not know the shape of (it is then simply never pinned by geometry).
 */
export function drawingDistance(name: string, pts: Px[], p: Px): number | null {
  const [a, b, c] = pts
  if (!a) return null
  switch (name) {
    case 'horizontalStraightLine':
    case 'levelLine':
      return Math.abs(p.y - a.y)
    case 'verticalStraightLine':
      return Math.abs(p.x - a.x)
    case 'segment':
    case 'arrow':
      return b ? distToSegment(p, a, b) : null
    case 'rayLine':
      return b ? distToRay(p, a, b) : null
    case 'straightLine':
      return b ? distToLine(p, a, b) : null
    case 'parallelStraightLine': {
      if (!b) return null
      const main = distToLine(p, a, b)
      if (!c) return main
      // The offset line runs through the third point, parallel to the first two.
      return Math.min(main, distToLine(p, c, { x: c.x + (b.x - a.x), y: c.y + (b.y - a.y) }))
    }
    case 'rectangle':
    case 'fibRetracement':
      return b ? distToBox(p, a, b) : null
    default:
      // Legacy single-point markers are glyphs, not lines: give them a glyph's reach.
      return LEG_MARKERS.includes(name) ? Math.max(0, Math.hypot(p.x - a.x, p.y - a.y) - 4) : null
  }
}

/** The nearest drawing within `tol`, preferring the later (drawn on top) of a tie; null for none. */
export function nearestDrawing(
  candidates: Array<{ id: string; name: string; pts: Px[] }>,
  p: Px,
  tol = PIN_HIT_TOL,
): string | null {
  let best: { id: string; d: number } | null = null
  for (const cand of candidates) {
    const d = drawingDistance(cand.name, cand.pts, p)
    if (d == null || d > tol) continue
    if (!best || d <= best.d) best = { id: cand.id, d }
  }
  return best?.id ?? null
}
